"use client";

import React, { useState, useEffect, useCallback } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "../../ui/card";
import { Button } from "../../ui/button";
import { ArrowLeft, Save, Loader2, MessageSquare } from "lucide-react";
import { ReviewComment } from "../../shared/ReviewComment";
import { Badge } from "../../ui/badge";
import { Label } from "../../ui/label";
import { DocumentManager } from "../../planning/DocumentManager";
import { getCompanyName } from "@/lib/textron-data-processor";
import { SHAREPOINT_FY_YEAR } from "@/constants/sharepoint-config";

interface ArmsLengthAssessmentSubstantiveProps {
  onBack?: () => void;
}

interface QuestionResponse {
  textResponse?: { html: string; plainText: string } | null;
  documents?: Array<{ id: string; type: string; name: string; content?: string; url?: string }>;
}

const DEFAULT_AREA_PREFIX = "RPT";
const QUESTION_ID = "arms-length-procedures";
const QUESTION = "Document the procedures performed by you to verify the respective ABCOTD.";

export default function ArmsLengthAssessmentSubstantive({
  onBack,
}: ArmsLengthAssessmentSubstantiveProps) {
  const [companyName, setCompanyName] = useState<string>("");
  const [workspaceName, setWorkspaceName] = useState<string>("");
  const [areaPrefix, setAreaPrefix] = useState<string>(DEFAULT_AREA_PREFIX);
  const [questionResponse, setQuestionResponse] = useState<QuestionResponse>({});
  const [inferenceQuestion, setInferenceQuestion] = useState<QuestionResponse>({});
  const [saving, setSaving] = useState(false);
  const [saveStatus, setSaveStatus] = useState<'idle' | 'saving' | 'saved' | 'error'>('idle');
  const [saveError, setSaveError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [reviewCommentQuestionId, setReviewCommentQuestionId] = useState<string | null>(null);

  // Load company name
  useEffect(() => {
    const loadCompanyName = async () => {
      try {
        const name = await getCompanyName("combined");
        setCompanyName(name || "ABC Private Limited");

      } catch (error) {
        console.warn('Failed to load company name, using fallback');
        setCompanyName("ABC Private Limited");
      }
    };
    loadCompanyName();
  }, []);

  // Load workspace name and area prefix from Sections.json
  useEffect(() => {
    const loadWorkspaceInfo = async () => {
      try {
        if ((window as any).sharePointAPI?.downloadSectionsJson) {
          const result = await (window as any).sharePointAPI.downloadSectionsJson();
          if (result?.success && result?.data) {
            const sectionsData = result.data;
            // Check all areas (Planning, Execution, ConcludingProcedures) to find the workspace
            const areas = ["Planning", "Execution", "ConcludingProcedures"] as const;
            for (const areaKey of areas) {
              if (sectionsData[areaKey]) {
                // Look for "Related Party Disclosure" workspace in this area
                for (const [wsName, wsPrefix] of Object.entries(sectionsData[areaKey])) {
                  if (wsName === "Related Party Disclosure") {
                    setWorkspaceName(wsName);
                    setAreaPrefix(wsPrefix as string);
                    return; // Found it, stop searching
                  }
                }
              }
            }
            // If not found, log a warning but use defaults
            console.warn(`Related Party Disclosure workspace not found in Sections.json, using defaults`);
            setWorkspaceName("Related Party Disclosure");
          }
        } else {
          // Fallback if API not available
          setWorkspaceName("Related Party Disclosure");
        }
      } catch (error) {
        console.warn(`Failed to load workspace info from Sections.json, using defaults:`, error);
        setWorkspaceName("Related Party Disclosure");
      }
    };
    loadWorkspaceInfo();
  }, []);

  // Load saved responses
  useEffect(() => {
    const loadSavedResponses = async () => {
      if (!companyName || !areaPrefix) {
        setLoading(false);
        return;
      }

      try {
        const fileName = `Execution_${areaPrefix}_Responses.json`;
        const result = await (window as any).sharePointAPI?.downloadClientJson?.(fileName);

        if (result?.success && result?.data?.substantiveProcedures) {
          const savedData = result.data.substantiveProcedures[QUESTION_ID];
          if (savedData) {
            setQuestionResponse({
              textResponse: savedData.textResponse || null,
              documents: (savedData.documents || []).map((docName: string) => ({
                id: `doc_${docName}`,
                type: 'linked',
                name: docName,
              })),
            });
          }
          
          // Load inference question
          const inferenceId = "ARMS-LENGTH-INFERENCE";
          const inferenceData = result.data.substantiveProcedures[inferenceId];
          if (inferenceData) {
            setInferenceQuestion({
              textResponse: inferenceData.textResponse || null,
              documents: (inferenceData.documents || []).map((docName: string) => ({
                id: `doc_${docName}`,
                type: 'linked',
                name: docName,
              })),
            });
          }
        }
      } catch (error) {
        console.error("Failed to load saved responses:", error);
      } finally {
        setLoading(false);
      }
    };

    loadSavedResponses();
  }, [companyName, areaPrefix]);

  const handleTextResponseChange = (textResponse: { html: string; plainText: string } | null) => {
    setQuestionResponse((prev) => ({
      ...prev,
      textResponse,
    }));
  };

  const handleDocumentsChange = (documents: Array<{ id: string; type: string; name: string; content?: string; url?: string }>) => {
    setQuestionResponse((prev) => ({
      ...prev,
      documents,
    }));
  };

  const handleSave = useCallback(async () => {
    if (!companyName || !areaPrefix) {
      setSaveError("Missing required data (company name or area prefix)");
      setSaveStatus('error');
      return;
    }

    // Auto-save any pending textbox content before processing responses
    const documentManagers = document.querySelectorAll('[data-document-manager]');
    for (const manager of Array.from(documentManagers)) {
      const itemId = manager.getAttribute('data-item-id');
      if (itemId) {
        const autoSaveMethod = (window as any)[`autoSaveTextbox_${itemId}`];
        if (autoSaveMethod && typeof autoSaveMethod === 'function') {
          autoSaveMethod();
        }
      }
    }

    // Wait for auto-save to complete
    await new Promise(resolve => setTimeout(resolve, 1500));

    setSaving(true);
    setSaveStatus('saving');
    setSaveError(null);

    try {
      const fileName = `Execution_${areaPrefix}_Responses.json`;

      // Load existing file first
      let existingData: any = {};
      try {
        const downloadResult = await (window as any).sharePointAPI?.downloadClientJson?.(fileName);
        if (downloadResult?.success && downloadResult?.data) {
          existingData = downloadResult.data;
        }
      } catch (error) {
        console.warn(`Failed to download existing ${fileName}, starting from empty`, error);
      }

      if (!existingData || typeof existingData !== "object" || Array.isArray(existingData)) {
        existingData = {};
      }

      if (!existingData.substantiveProcedures) {
        existingData.substantiveProcedures = {};
      }

      // Save the response
      const documentNames = (questionResponse.documents || []).map((d) => d.name || d);

      existingData.substantiveProcedures[QUESTION_ID] = {
        selectedOption: "",
        textResponse: questionResponse.textResponse || null,
        documents: documentNames,
        table: null,
        lastUpdated: new Date().toISOString(),
        companyName,
      };

      // Save inference question - query db.json for section field like CustomProcedure
      const inferenceId = "ARMS-LENGTH-INFERENCE";
      const sectionToFind = `Execution_${areaPrefix}_${inferenceId}`;
      let inferenceDocumentNames: string[] = [];
      try {
        if ((window as any).sharePointAPI?.loadDocumentListBySection) {
          const dbResult = await (window as any).sharePointAPI.loadDocumentListBySection(sectionToFind);
          if (dbResult?.success && dbResult?.data && Array.isArray(dbResult.data)) {
            inferenceDocumentNames = dbResult.data
              .map((file: any) => file.name || '')
              .filter((name: string) => name && name.trim().length > 0);
          }
        }
      } catch (error) {
        console.warn(`Failed to query db.json for section ${sectionToFind}:`, error);
      }

      const inferenceTextboxFromLocalStorage = localStorage.getItem(`textbox_${inferenceId}`);
      const hasInferenceTextboxContent = inferenceTextboxFromLocalStorage && inferenceTextboxFromLocalStorage.trim().length > 0;
      
      let inferenceTextResponse = inferenceQuestion.textResponse || null;
      if (hasInferenceTextboxContent) {
        inferenceTextResponse = {
          html: inferenceTextboxFromLocalStorage,
          plainText: inferenceTextboxFromLocalStorage.replace(/<[^>]*>/g, '')
        };
      }

      existingData.substantiveProcedures[inferenceId] = {
        selectedOption: "",
        textResponse: inferenceTextResponse,
        documents: inferenceDocumentNames,
        table: null,
        lastUpdated: new Date().toISOString(),
        companyName,
      };

      // Upload to SharePoint
      const jsonString = JSON.stringify(existingData, null, 2);
      const base64Content = btoa(unescape(encodeURIComponent(jsonString)));

      const uploadResult = await (window as any).sharePointAPI?.uploadFile?.({
        fileContent: base64Content,
        fileName,
        referenceValue: `Execution ${areaPrefix} Substantive Responses`,
        folderName: "juggernaut",
        fyYear: String(SHAREPOINT_FY_YEAR),
        sectionList: [`Execution_${areaPrefix}_Responses`],
      });

      if (!uploadResult || !uploadResult.success) {
        throw new Error(uploadResult?.error || "Failed to save responses");
      }

      setSaveStatus('saved');
      setTimeout(() => setSaveStatus('idle'), 3000);
    } catch (error) {
      console.error("Failed to save responses:", error);
      setSaveError(error instanceof Error ? error.message : "Unknown error");
      setSaveStatus('error');
    } finally {
      setSaving(false);
    }
  }, [companyName, areaPrefix, questionResponse, inferenceQuestion]);

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-center py-12">
          <Loader2 className="h-8 w-8 animate-spin text-white/60" />
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-white">Arms-Length Assessment - Substantive Procedures</h2>
          <p className="text-gray-400">Document procedures for arms-length assessment verification</p>
        </div>
        {onBack && (
          <Button variant="outline" onClick={onBack}>
            <ArrowLeft className="h-4 w-4 mr-2" /> Back
          </Button>
        )}
      </div>

      <Card className="border-white/10 bg-black/40">
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="text-white">Arms-Length Assessment</CardTitle>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => setReviewCommentQuestionId("RELATED_PARTY_DISCLOSURE_ARMS_LENGTH_ASSESSMENT")}
              className="flex-shrink-0 p-1 h-7 w-7 text-yellow-400 hover:text-yellow-300 hover:bg-white/10"
              title="Review Comment"
            >
              <MessageSquare className="h-4 w-4" />
            </Button>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-3">
            <Label className="text-sm font-medium text-white">
              {QUESTION}
            </Label>
            <div className="mt-4">
              <DocumentManager
                sectionKey={`execution-substantive:${workspaceName || "Related Party Disclosure"}`}
                itemId={QUESTION_ID}
                itemQuestion={QUESTION}
                itemTextResponse={questionResponse.textResponse || null}
                itemDocuments={questionResponse.documents || []}
                onDocumentsChange={handleDocumentsChange}
                onTextResponseChange={handleTextResponseChange}
                disabled={false}
              />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Inference and Conclusion Question */}
      <Card className="border-white/10 bg-black/40">
        <CardHeader>
          <CardTitle className="text-white">Document the inference and conclusion from the above procedure performed.</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <DocumentManager
            sectionKey={`execution-substantive:${workspaceName || "Related Party Disclosure"}`}
            itemId="ARMS-LENGTH-INFERENCE"
            itemQuestion="Document the inference and conclusion from the above procedure performed."
            itemTextResponse={inferenceQuestion.textResponse || null}
            itemDocuments={inferenceQuestion.documents || []}
            onDocumentsChange={(docs) => setInferenceQuestion({ ...inferenceQuestion, documents: docs })}
            onTextResponseChange={(textResp) => setInferenceQuestion({ ...inferenceQuestion, textResponse: textResp })}
            disabled={false}
          />
        </CardContent>
      </Card>

      <div className="flex justify-end items-center gap-4 pt-6">
        {saveStatus === 'saved' && (
          <Badge variant="outline" className="border-green-600 text-green-600">
            Saved successfully
          </Badge>
        )}
        {saveStatus === 'error' && saveError && (
          <Badge variant="destructive" className="text-red-600">
            Error: {saveError}
          </Badge>
        )}
        <Button
          onClick={handleSave}
          className="flex items-center gap-2"
          disabled={saving || !companyName || !areaPrefix}
        >
          {saving ? (
            <>
              <Loader2 className="h-4 w-4 animate-spin" />
              Saving...
            </>
          ) : (
            <>
              <Save className="h-4 w-4" />
              Save Responses
            </>
          )}
        </Button>
      </div>

      {/* Review Comment Modal */}
      {reviewCommentQuestionId && (
        <ReviewComment
          questionId={reviewCommentQuestionId}
          questionText="Arms-Length Assessment"
          isOpen={!!reviewCommentQuestionId}
          onClose={() => setReviewCommentQuestionId(null)}
        />
      )}
    </div>
  );
}

